import { formatValue } from './format';
import type { Palette } from './palette';
import type { Block, MeasureFormat } from './types';

export type AxisScale = { min?: number; max?: number; zero?: boolean };

export type ReferenceLine = { value: number; label?: string; axis?: 'right' };

export const axisScale = (block?: Block): AxisScale => (block?.scale ?? {}) as AxisScale;

export const scaleBounds = (scale: AxisScale) => ({
  ...(typeof scale.min === 'number' ? { min: scale.min } : {}),
  ...(typeof scale.max === 'number' ? { max: scale.max } : {}),
});

export const referenceLines = (block?: Block): Array<ReferenceLine> =>
  (Array.isArray(block?.refs) ? (block!.refs as Array<{ value?: unknown; label?: string }>) : []).filter((ref): ref is ReferenceLine => typeof ref?.value === 'number');

export function referenceMarks(block: Block | undefined, palette: Palette, format: MeasureFormat | undefined, axis: 'main' | 'right' = 'main') {
  const refs = referenceLines(block).filter((ref) => (ref.axis === 'right') === (axis === 'right'));
  if (!refs.length) return {};
  return {
    markLine: {
      emphasis: { disabled: true },
      symbol: 'none',
      lineStyle: { color: palette.ink3, type: [4, 4], width: 1 },
      label: { position: 'insideEndTop', color: palette.ink2, fontSize: 10.5, fontFamily: palette.sans },
      data: refs.map((ref) => ({ yAxis: ref.value, label: { formatter: ref.label ?? formatValue(ref.value, format) } })),
    },
  };
}

const text = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : undefined);

export function yAxisName(block: Block | undefined, fallback: Record<string, unknown>, palette: Palette) {
  const title = text(block?.yTitle);
  return title ? { name: title, nameTextStyle: { color: palette.ink3, fontSize: 11, align: 'left' } } : fallback;
}

export function xAxisName(block: Block | undefined, palette: Palette) {
  const title = text(block?.xTitle);
  return title ? { name: title, nameLocation: 'middle', nameGap: block?.xLabels === 'angled' ? 44 : 26, nameTextStyle: { color: palette.ink3, fontSize: 11 } } : {};
}

export const xTitleRoom = (block: Block | undefined) => (text(block?.xTitle) ? (block?.xLabels === 'angled' ? 40 : 22) : 0);

export const xLabelSpacing = (block: Block | undefined) =>
  block?.xLabels === 'all' ? { interval: 0, hideOverlap: false } : block?.xLabels === 'angled' ? { interval: 0, rotate: 45, hideOverlap: false } : {};

export const curve = (block: Block | undefined) => (block?.curve === 'smooth' ? { smooth: 0.35 } : {});

const VALUE_LABELS = ['latest', 'all', 'top'] as const;

function labelledPoints(mode: (typeof VALUE_LABELS)[number], values: Array<number | null>): Set<number> {
  const present = values.flatMap((value, index) => (value === null || value === undefined ? [] : [index]));
  if (mode === 'all') return new Set(present);
  if (mode === 'latest') return new Set(present.slice(-1));
  return new Set([...present].sort((a, b) => (values[b] as number) - (values[a] as number)).slice(0, 3));
}

export function valueLabels(block: Block | undefined, values: Array<number | null>, format: MeasureFormat | undefined, palette: Palette, position = 'top') {
  const mode = VALUE_LABELS.find((entry) => entry === block?.values);
  if (!mode) return {};
  const shown = labelledPoints(mode, values);
  return {
    label: {
      show: true,
      position,
      distance: 4,
      fontSize: 10.5,
      color: palette.ink2,
      fontFamily: palette.sans,
      formatter: ({ dataIndex }: { dataIndex: number }) => (shown.has(dataIndex) ? formatValue(values[dataIndex], format) : ''),
    },
  };
}

export function endLabelMargin(values: Array<number | null>, labelWidth: number, width: number) {
  const last = values.reduce((found, value, index) => value === null ? found : index, -1);
  if (last < 0) return 12;
  const tail = values.length > 1 ? 1 - last / (values.length - 1) : 0;
  return Math.max(12, labelWidth - tail * Math.max(0, width - labelWidth - 80));
}
